(() => {
  if (window.nomkunCommerceInitialized) return;
  window.nomkunCommerceInitialized = true;
  const siblingRequests = new Map();
  const siblingMarkup = (block) => {
    const url = new URL(block.dataset.searchUrl, location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('view', 'nomkun-siblings');
    url.searchParams.set('q', block.dataset.title);
    url.searchParams.set('options[prefix]', 'none');
    const key = url.href;
    if (!siblingRequests.has(key)) {
      siblingRequests.set(key, (async () => {
        const links = [];
        const seenPages = new Set();
        let next = url;
        while (next && !seenPages.has(next.href)) {
          seenPages.add(next.href);
          const response = await fetch(next, {headers:{Accept:'text/html'}});
          if (!response.ok) throw new Error('siblings');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!html.querySelector('[data-nk-sibling-results]')) throw new Error('siblings');
          html.querySelectorAll('[data-nk-sibling-product]').forEach(link => links.push(link.outerHTML));
          const more = html.querySelector('[data-nk-sibling-next]')?.getAttribute('href');
          next = more ? new URL(more, location.origin) : null;
          if (next && next.origin !== location.origin) throw new Error('siblings');
        }
        return links.join('');
      })().catch(error => { siblingRequests.delete(key); throw error; }));
    }
    return siblingRequests.get(key);
  };
  const loadSiblings = (root = document) => {
    root.querySelectorAll('[data-nk-siblings]').forEach(async block => {
      if (block.dataset.loaded) return;
      block.dataset.loaded = 'true';
      try {
        const markup = await siblingMarkup(block);
        if (!block.isConnected) return;
        const list = block.querySelector('[data-nk-sibling-list]');
        list.innerHTML = markup;
        const current = new URL(block.dataset.currentUrl, location.origin).pathname;
        list.querySelectorAll('a').forEach(link => {
          if (new URL(link.href).pathname === current) link.setAttribute('aria-current', 'page');
        });
        block.hidden = list.children.length < 2;
      } catch { delete block.dataset.loaded; }
    });
  };
  const variantRequests = new Map();
  const favoriteStorageKey = 'nomkun.favorites.v1';
  const favoriteSet = () => {
    try { return new Set(JSON.parse(localStorage.getItem(favoriteStorageKey) || '[]')); }
    catch { return new Set(); }
  };
  const syncFavorites = () => {
    const saved = favoriteSet();
    document.querySelectorAll('[data-nk-wishlist]').forEach(button => {
      const active = saved.has(button.dataset.nkWishlist);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? button.dataset.labelRemove : button.dataset.labelAdd);
    });
  };
  const setMainPhoto = (gallery, source, alt) => {
    const main = gallery.querySelector('.nk-main-photo');
    if (!main) return;
    main.srcset = '';
    main.src = source;
    main.alt = alt || '';
  };
  const setCurrentThumb = (gallery, selected) => {
    gallery.querySelectorAll('.nk-gallery-thumb').forEach(button => {
      if (button === selected) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  };
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const favorite = target.closest('[data-nk-wishlist]');
    if (favorite) {
      const saved = favoriteSet();
      const key = favorite.dataset.nkWishlist;
      if (saved.has(key)) saved.delete(key); else saved.add(key);
      try { localStorage.setItem(favoriteStorageKey, JSON.stringify([...saved])); } catch {}
      syncFavorites();
    }
    const thumb = target.closest('[data-nk-gallery-thumb]');
    if (thumb) {
      const gallery = thumb.closest('[data-nk-gallery]');
      setMainPhoto(gallery, thumb.dataset.image, thumb.dataset.imageAlt);
      setCurrentThumb(gallery, thumb);
    }
    const previewThumb = target.closest('[data-nk-preview-thumb]');
    if (previewThumb) {
      const gallery = previewThumb.closest('[data-nk-gallery]');
      const sprite = gallery.querySelector('[data-nk-gallery-sprite]');
      const main = gallery.querySelector('.nk-main-photo');
      const frame = previewThumb.dataset.nkPreviewThumb;
      if (main && sprite) {
        main.hidden = frame !== 'main';
        sprite.hidden = frame === 'main';
        sprite.style.setProperty('--nk-gallery-crop', frame === '2' ? '12%' : '4%');
        if (frame !== 'main') sprite.style.setProperty('--nk-frame', `-${((Number(frame) - 0.5) / 3) * 100}%`);
      }
      setCurrentThumb(gallery, previewThumb);
    }
    const zoom = target.closest('[data-nk-zoom]');
    if (zoom) {
      const section = zoom.closest('[data-nk-product]');
      const main = section.querySelector('.nk-main-photo');
      const wrapper = section.closest('.shopify-section') || section.parentElement;
      const dialog = wrapper.querySelector('[data-nk-zoom-dialog]');
      if ((zoom.dataset.image || main) && dialog) {
        const image = dialog.querySelector('[data-nk-zoom-image]');
        image.src = zoom.dataset.image || main.currentSrc || main.src;
        image.alt = zoom.dataset.imageAlt || main?.alt || '';
        dialog.showModal();
      }
    }
  });
  const updateVariant = async input => {
    const root = input.closest('[data-nk-product]');
    if (!root || root.hasAttribute('data-nk-preview')) return;
    const sectionId = root.dataset.sectionId;
    variantRequests.get(sectionId)?.abort();
    const controller = new AbortController();
    variantRequests.set(sectionId, controller);
    const button = root.querySelector('.nk-add-to-cart');
    if (button) button.disabled = true;
    root.setAttribute('aria-busy', 'true');
    const selected = [...root.querySelectorAll('[data-nk-option]:checked')].map(option => option.dataset.optionValueId).filter(Boolean);
    const url = new URL(window.location.href);
    const nextProduct = input.dataset.productUrl;
    if (nextProduct && nextProduct !== root.dataset.productUrl && !url.hostname.endsWith('.shopifypreview.com')) url.pathname = new URL(nextProduct, location.origin).pathname;
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', selected.join(','));
    url.searchParams.set('section_id', sectionId);
    try {
      const response = await fetch(url, {signal: controller.signal, headers: {Accept: 'text/html'}});
      if (!response.ok) throw new Error('variant');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-nk-product]');
      if (!next || controller.signal.aborted) throw new Error('variant');
      const inputId = input.id;
      root.replaceWith(next);
      loadSiblings(next);
      const currentVariant = next.querySelector('input[name="id"]')?.value;
      url.searchParams.delete('section_id');
      if (currentVariant) {
        url.searchParams.delete('option_values');
        url.searchParams.set('variant', currentVariant);
      }
      history.replaceState({}, '', url);
      document.getElementById(inputId)?.focus({preventScroll:true});
      syncFavorites();
    } catch (error) {
      if (error.name !== 'AbortError') {
        const status = root.querySelector('[data-nk-variant-status]');
        if (status) { status.textContent = status.dataset.error; status.classList.remove('visually-hidden'); }
      }
    } finally {
      if (!controller.signal.aborted) root.removeAttribute('aria-busy');
    }
  };
  const formatNumber = value => new Intl.NumberFormat('tr-TR', {maximumFractionDigits:0}).format(value);
  const updateRange = (form, changed) => {
    const min = form.querySelector('[data-nk-range-min]');
    const max = form.querySelector('[data-nk-range-max]');
    if (!min || !max) return;
    if (Number(min.value) > Number(max.value)) {
      if (changed === min) max.value = min.value; else min.value = max.value;
    }
    const minOutput = form.querySelector('[data-nk-min-output]');
    const maxOutput = form.querySelector('[data-nk-max-output]');
    if (minOutput) minOutput.textContent = formatNumber(Number(min.value));
    if (maxOutput) maxOutput.textContent = formatNumber(Number(max.value));
  };
  const updateDemoCollection = root => {
    const form = root.querySelector('[data-nk-demo-filters]');
    const grid = root.querySelector('[data-nk-demo-grid]');
    if (!form || !grid) return;
    const values = new FormData(form);
    const category = values.get('demo_category') || 'all';
    const sizes = values.getAll('demo_size');
    const colors = values.getAll('demo_color');
    const min = Number(values.get('demo_min') || 0);
    const max = Number(values.get('demo_max') || Infinity);
    const cards = [...grid.querySelectorAll('[data-nk-demo-card]')];
    let visible = 0;
    cards.forEach((card, index) => {
      if (!card.dataset.initialOrder) card.dataset.initialOrder = String(index + 1);
      const categoryMatch = category === 'all' || (card.dataset.categories || '').split(' ').includes(category);
      const sizeMatch = !sizes.length || sizes.some(size => (card.dataset.sizes || '').split(' ').includes(size));
      const colorMatch = !colors.length || colors.includes(card.dataset.color);
      const price = Number(card.dataset.price);
      const show = categoryMatch && sizeMatch && colorMatch && price >= min && price <= max;
      card.hidden = !show;
      if (show) visible++;
    });
    const sort = root.querySelector('[data-nk-demo-sort]')?.value || 'featured';
    cards.sort((a,b) => {
      if (sort === 'price-ascending') return Number(a.dataset.price) - Number(b.dataset.price);
      if (sort === 'price-descending') return Number(b.dataset.price) - Number(a.dataset.price);
      if (sort === 'title-ascending') return a.querySelector('.nk-card-title').textContent.localeCompare(b.querySelector('.nk-card-title').textContent, 'tr');
      return Number(a.dataset.initialOrder) - Number(b.dataset.initialOrder);
    }).forEach(card => grid.append(card));
    const count = root.querySelector('[data-nk-product-count]');
    if (count) count.textContent = `${visible} ${count.dataset.countLabel}`;
    const empty = root.querySelector('[data-nk-demo-empty]');
    if (empty) empty.hidden = visible > 0;
  };
  document.addEventListener('input', event => {
    if (!(event.target instanceof Element)) return;
    const input = event.target;
    if (input.matches('[data-nk-range-min], [data-nk-range-max]')) {
      const form = input.closest('form');
      updateRange(form, input);
      const root = form.closest('[data-nk-demo-collection]');
      if (root) updateDemoCollection(root);
    }
  });
  document.addEventListener('change', event => {
    if (!(event.target instanceof Element)) return;
    const input = event.target;
    if (input.matches('[data-nk-option]')) updateVariant(input);
    if (input.closest('[data-nk-demo-filters]') || input.matches('[data-nk-demo-sort]')) updateDemoCollection(input.closest('[data-nk-demo-collection]'));
    if (input.matches('[data-nk-sort-select]')) {
      const form = document.getElementById(input.dataset.formId);
      if (form) { form.querySelector('[data-nk-sort-hidden]').value = input.value; form.requestSubmit(); }
    } else if (input.closest('[data-nk-native-filters]')) input.closest('form').requestSubmit();
  });
  document.addEventListener('reset', event => {
    if (event.target instanceof Element && event.target.matches('[data-nk-demo-filters]')) requestAnimationFrame(() => {
      updateRange(event.target);
      updateDemoCollection(event.target.closest('[data-nk-demo-collection]'));
    });
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.nk-product-form')) return;
    event.preventDefault();
    const root = form.closest('[data-nk-product]');
    const button = form.querySelector('.nk-add-to-cart');
    if (!button || button.disabled || root.hasAttribute('data-nk-preview')) return;
    const status = form.querySelector('[data-nk-cart-status]');
    const next = form.querySelector('.nk-cart-next');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    try {
      const notification = document.querySelector('cart-notification');
      const body = new FormData(form);
      if (notification) {
        body.set('sections', 'cart-notification-content,cart-notification-crosssell-products');
        body.set('sections_url', window.location.pathname);
      }
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {method:'POST', body, headers:{Accept:'application/json'}});
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : status?.dataset.error || '');
      if (status) status.textContent = status.dataset.success;
      if (next) next.hidden = false;
      document.dispatchEvent(new CustomEvent('cart:updated', {detail:data}));
      if (notification) {
        if (typeof notification.renderContents !== 'function') window.triggerScripts?.();
        const ready = await Promise.race([
          customElements.whenDefined('cart-notification').then(() => true),
          new Promise(resolve => setTimeout(() => resolve(false), 6000))
        ]);
        if (ready) {
          notification.setActiveElement(button);
          notification.renderContents(data);
        }
      }
    } catch (error) {
      if (status) status.textContent = error.message || status.dataset.error;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
  const init = () => {
    syncFavorites();
    loadSiblings();
    document.querySelectorAll('[data-nk-mobile-filters]').forEach(details => {
      if (!details.dataset.nkInitialized) {
        details.dataset.nkInitialized = 'true';
        if (matchMedia('(max-width:749px)').matches) details.open = false;
      }
    });
    document.querySelectorAll('[data-nk-demo-collection]').forEach(updateDemoCollection);
  };
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('storage', event => { if (event.key === favoriteStorageKey) syncFavorites(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
})();
